import type { TodoDto } from "../../../services/vorgangService";
import type { KontaktRef, RisikoRef, VertragRef } from "../detail/linkSlots";
import { slotsFromDto } from "../detail/linkSlots";
import { toIsoDate } from "../detail/twvFrist";

/**
 * To-dos zusammenführen — die Ableitung, ohne React (P6, PO 16.09.2026).
 *
 * ══ WOZU EINE EIGENE DATEI ═════════════════════════════════════════════════
 * Der Dialog zeigt sieben Felder, und jedes einzelne entsteht aus BEIDEN
 * To-dos: Betreff vom Ziel, Rollen als Vereinigung, Kunde als
 * Entweder-oder, TWV und Frist als das frühere der beiden Daten. Das ist die
 * eigentliche Arbeit; das Zeichnen ist der leichte Teil. In einer Komponente
 * ließe sich davon nichts prüfen, ohne einen Browser zu starten.
 *
 * ══ VEREINIGEN, NICHT ERSETZEN ═════════════════════════════════════════════
 * Zusammenführen heißt: NICHTS geht verloren. Wer an einem der beiden To-dos
 * hing, hängt danach am zusammengeführten. Das gilt für Bearbeiter, zu
 * Informierende, Risiken und Verträge.
 *
 * Genau EIN Feld kann das nicht: der Kunde. Ein To-do hat einen führenden
 * Kunden, nicht zwei. Tragen beide Seiten VERSCHIEDENE Kunden, ist das kein
 * Detail, das sich still entscheiden lässt — dann fragt der Dialog
 * (`kundenKonflikt`), und ohne Antwort geht es nicht weiter.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Was der Dialog anbietet — die Vereinigung beider Seiten. */
export interface ZusammenfuehrenAuswahl {
    hauptverantwortlich: string[];
    bearbeiter: string[];
    zuInformierende: string[];
    /** 0, 1 oder 2 Einträge — mehr als einer heißt Konflikt. */
    kontakte: KontaktRef[];
    risiken: RisikoRef[];
    vertraege: VertragRef[];
    /**
     * Beide Seiten tragen einen Kunden, und es sind VERSCHIEDENE. Der Dialog
     * sagt das im Klartext und verlangt eine Auswahl.
     */
    kundenKonflikt: boolean;
}

/** Was gespeichert würde — der Rumpf für `POST /todo/:id/merge`. */
export interface ZusammenfuehrenEntwurf {
    titel: string;
    hauptverantwortlich: string[];
    bearbeiter: string[];
    zuInformierende: string[];
    kontaktId: number | null;
    risikoIds: number[];
    vertragIds: number[];
    /** 'YYYY-MM-DD' oder null. */
    twv: string | null;
    frist: string | null;
}

/** Ids (oder Objekt-Einträge) → deduplizierte Liste in Reihenfolge des Auftretens. */
function vereinigeIds(...listen: Array<unknown[] | null | undefined>): string[] {
    const out: string[] = [];
    for (const liste of listen ?? []) {
        for (const eintrag of liste ?? []) {
            const id =
                typeof eintrag === "string"
                    ? eintrag
                    : ((eintrag as any)?.userId ?? (eintrag as any)?.id ?? (eintrag as any)?.kuerzel ?? null);
            if (id && !out.includes(id)) out.push(id);
        }
    }
    return out;
}

/** Bezugs-Objekte (Risiko, Vertrag, Kontakt) → dedupliziert über die `id`. */
function vereinigeRefs<T extends { id: number }>(...listen: Array<T[] | null | undefined>): T[] {
    const out: T[] = [];
    for (const liste of listen ?? []) {
        for (const ref of liste ?? []) {
            if (ref && !out.some((x) => x.id === ref.id)) out.push(ref);
        }
    }
    return out;
}

/**
 * Das FRÜHERE der beiden Daten — und zwar das frühere von denen, die es gibt.
 *
 * Ein leeres Feld ist kein „unendlich fern", sondern „nicht gesetzt": trägt nur
 * eine Seite eine Frist, gilt sie. Die andere Lesart („leer gewinnt") ließe die
 * Frist beim Zusammenführen still verfallen.
 */
export function frueheresDatum(a?: string | null, b?: string | null): string | null {
    const x = toIsoDate(a) || null;
    const y = toIsoDate(b) || null;
    if (!x) return y;
    if (!y) return x;
    return x <= y ? x : y;
}

/** Die Vereinigung beider Seiten — was der Dialog zur Auswahl stellt. */
export function zusammenfuehrenAuswahl(ziel: TodoDto, quelle: TodoDto): ZusammenfuehrenAuswahl {
    const zs = slotsFromDto(ziel);
    const qs = slotsFromDto(quelle);
    const kontakte = vereinigeRefs<KontaktRef>(
        zs.kontakt ? [zs.kontakt] : [],
        qs.kontakt ? [qs.kontakt] : [],
    );
    return {
        hauptverantwortlich: vereinigeIds(ziel.hauptverantwortlich, quelle.hauptverantwortlich),
        bearbeiter: vereinigeIds(ziel.bearbeiter, quelle.bearbeiter),
        zuInformierende: vereinigeIds(ziel.zuInformierende, quelle.zuInformierende),
        kontakte,
        risiken: vereinigeRefs<RisikoRef>(zs.risiken, qs.risiken),
        vertraege: vereinigeRefs<VertragRef>(zs.vertraege, qs.vertraege),
        kundenKonflikt: kontakte.length > 1,
    };
}

/**
 * Die Vorbelegung des Dialogs.
 *
 * `ziel` ist das gewählte (oder beim Ziehen: das getroffene) To-do — es bleibt
 * bestehen, `quelle` (das angeklickte bzw. gezogene) geht darin auf
 * (PO 28.09.2026). Sein Betreff steht deshalb im Feld, und sein Hauptverantwortlicher ist
 * vorausgewählt (hat er keinen, tritt der der Quelle ein — sonst stünde das
 * zusammengeführte To-do ohne da, obwohl eine Seite jemanden nannte).
 *
 * Bearbeiter, zu Informierende, Risiken und Verträge sind VOLLSTÄNDIG
 * vorbelegt: die Vereinigung ist der Normalfall, das Abwählen die Ausnahme.
 *
 * Der Kunde bleibt bei einem Konflikt bewusst LEER — eine stille Vorauswahl
 * zwischen zwei Kunden wäre geraten, und man sähe es dem Dialog nicht an.
 *
 * ══ WESSEN BETREFF? (T-15, 28.09.2026) ════════════════════════════════════
 * Beim ZIEHEN gilt der Betreff des gezogenen To-dos (`betreffVon: "quelle"`):
 * „todos zusammen schieben … das zusammengeführte To-do trägt den Betreff des
 * gezogenen". Technisch bleibt trotzdem das getroffene To-do bestehen (Id,
 * Verlauf) — es bekommt nur den Betreff der Quelle. Beim Weg über
 * „⋯ → To-do zusammenführen…" bleibt es beim Betreff des Ziels (PO oben),
 * deshalb ist `"ziel"` der Standard. Ist der Betreff der Quelle leer, springt
 * der des Ziels ein — sonst wäre der Knopf sofort gesperrt.
 */
export function zusammenfuehrenEntwurf(
    ziel: TodoDto,
    quelle: TodoDto,
    opts: { betreffVon?: "ziel" | "quelle" } = {},
): ZusammenfuehrenEntwurf {
    const auswahl = zusammenfuehrenAuswahl(ziel, quelle);
    const hv = (ziel.hauptverantwortlich ?? []).length
        ? vereinigeIds(ziel.hauptverantwortlich)
        : vereinigeIds(quelle.hauptverantwortlich);
    return {
        titel:
            opts.betreffVon === "quelle"
                ? quelle.titel?.trim() || ziel.titel || ""
                : (ziel.titel ?? ""),
        hauptverantwortlich: hv,
        bearbeiter: auswahl.bearbeiter,
        zuInformierende: auswahl.zuInformierende,
        kontaktId: auswahl.kundenKonflikt ? null : (auswahl.kontakte[0]?.id ?? null),
        risikoIds: auswahl.risiken.map((r) => r.id),
        vertragIds: auswahl.vertraege.map((v) => v.id),
        twv: frueheresDatum(ziel.twv, quelle.twv),
        frist: frueheresDatum(ziel.frist, quelle.frist),
    };
}

/**
 * Warum der Knopf nicht geht — oder `null`, wenn er geht.
 *
 * Als Text und nicht als `boolean`: ein ausgegrauter Knopf ohne Begründung ist
 * die häufigste Sackgasse dieser Anwendung.
 */
export function zusammenfuehrenFehler(
    entwurf: ZusammenfuehrenEntwurf,
    auswahl: ZusammenfuehrenAuswahl,
): string | null {
    if (!entwurf.titel.trim()) return "Bitte einen Betreff angeben.";
    if (auswahl.kundenKonflikt && entwurf.kontaktId == null) {
        return "Bitte entscheiden, welcher Kunde am zusammengeführten To-do stehen soll.";
    }
    return null;
}

/**
 * „Alles aus ‚B' wandert nach ‚A'. ‚B' verschwindet." — und, wenn der Betreff
 * im Feld nicht der des Ziels ist, wie das zusammengeführte To-do danach heißt.
 *
 * Das Ziel wird dabei immer über seinen EIGENEN Betreff benannt, nie über den
 * im Feld: sonst stünde beim Ziehen (Betreff der Quelle, T-15) oder nach einem
 * getippten Betreff „Alles aus ‚B' wandert nach ‚B'" da.
 */
export function zusammenfuehrenFolgeText(
    zielTitel: string,
    quelleTitel: string,
    neuerTitel?: string | null,
): string {
    const a = zielTitel.trim() || "diesem To-do";
    const b = quelleTitel.trim() || "dem anderen To-do";
    const satz = `Alles aus „${b}“ wandert nach „${a}“. „${b}“ verschwindet.`;
    const neu = (neuerTitel ?? "").trim();
    if (!neu || neu === zielTitel.trim()) return satz;
    return `${satz} Das zusammengeführte To-do heißt danach „${neu}“.`;
}
